import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, Clapperboard, Loader2, Play, RotateCw, X, Zap } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { cancelJob, errMsg, produceEstimate, startProduction } from '../../lib/aiStudioApi';
import { isRendering, jobRunning, jobStalled } from './useMovieProject';
import { useElapsed } from './LiveMedia';
import { BudgetEstimate, CostMeter } from './BudgetEstimate';
import { CostReceipt } from './CostReceipt';
import { notReady, ServiceNote, useStudioServices } from './useStudioServices';
import { cn } from '../../lib/utils';

function Step({ label, done, total, active }) {
  const complete = total > 0 && done >= total;
  return (
    <div className="flex items-center gap-2">
      <span className={cn('flex h-5 w-5 items-center justify-center rounded-full border text-[10px]',
        complete ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200' : active ? 'border-amber-400 text-amber-200' : 'border-neutral-700 text-neutral-500')}>
        {complete ? <Check size={11} /> : active ? <Loader2 size={11} className="animate-spin" /> : ''}
      </span>
      <span className="text-xs text-neutral-300">{label}</span>
      <span className="font-mono text-[10px] text-neutral-500">{done}/{total}</span>
    </div>
  );
}

function EstimateDialog({ open, onOpenChange, project, resolution, onStarted }) {
  const [episode, setEpisode] = useState(0);
  const [voiceover, setVoiceover] = useState(false);
  const [trailer, setTrailer] = useState(true);
  const [est, setEst] = useState(null);
  const [estErr, setEstErr] = useState('');
  const [starting, setStarting] = useState('');
  const budgetMode = project.engine_effective === 'budget';
  const down = notReady(useStudioServices(), budgetMode ? ['frames', 'motion', 'files'] : []);
  const mtv = project.format === 'mtv';
  const [budget, setBudget] = useState(Math.max(0.25, Number(project.budget_usd) || (mtv ? 1 : 1.6)));
  const eps = project.format === 'series' ? project.episodes_count || 1 : 1;
  const params = { resolution, episode, include_voiceover: voiceover, ...(budgetMode ? { budget_usd: budget } : {}) };
  useEffect(() => {
    if (!open) return;
    setEstErr('');
    produceEstimate(project.id, params).then(setEst).catch((e) => { setEst(null); setEstErr(errMsg(e)); });
  }, [open, episode, voiceover, resolution, budget, project.id, project.scenes?.length]); // eslint-disable-line react-hooks/exhaustive-deps
  async function go(anyway = false) {
    setStarting(anyway ? 'anyway' : 'normal');
    try {
      await startProduction(project.id, { ...params, include_trailer: trailer, ...(anyway ? { produce_anyway: true } : {}) });
      toast.success(anyway ? `Production started at ≈ $${est.needed_usd.toFixed(2)} — this run only` : 'Action! Production started — watch shots come alive');
      onStarted(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setStarting(''); }
  }
  const over = budgetMode && est && !est.full_fits && est.needed_usd > 0;
  const rows = est ? [
    ['Cast & location reference portraits', est.kit_references, est.kit_credits],
    ['Storyboard stills', est.stills, est.still_credits],
    [`Motion renders (${resolution})`, est.motion_shots, est.motion_credits],
    ['Voice-over recordings', est.voiceovers, est.voiceover_credits],
  ] : [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="studio-dark max-h-[85vh] max-w-lg overflow-y-auto border-neutral-800 bg-neutral-950 text-neutral-100" data-testid="produce-dialog">
        <DialogHeader><DialogTitle>Produce {mtv ? 'the music video' : episode ? `episode ${episode}` : eps > 1 ? 'the full series' : 'the film'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {eps > 1 && (
            <select value={episode} onChange={(e) => setEpisode(Number(e.target.value))} data-testid="produce-episode-select"
              className="w-full rounded-md border border-neutral-800 bg-neutral-900 px-2 py-2 text-sm">
              <option value={0}>All {eps} episodes</option>
              {Array.from({ length: eps }, (_, i) => <option key={i + 1} value={i + 1}>Episode {i + 1} only</option>)}
            </select>
          )}
          {!mtv && (
          <label className="flex items-center gap-2 text-xs text-neutral-300">
            <Checkbox checked={voiceover} onCheckedChange={(v) => setVoiceover(!!v)} data-testid="produce-voiceover-checkbox" />
            Also record voice-over with each character's cast voice
          </label>
          )}
          <label className="flex items-center gap-2 text-xs text-neutral-300">
            <Checkbox checked={trailer} onCheckedChange={(v) => setTrailer(!!v)} data-testid="produce-trailer-checkbox" />
            Then cut a free 20-second trailer <span className="text-emerald-300">· 0 cr</span>
          </label>
          {budgetMode ? <BudgetEstimate est={est} budget={budget} onBudget={setBudget} mtv={mtv} perEpisode={eps > 1 && !episode} /> : (
          <div className="rounded-lg border border-neutral-800" data-testid="produce-estimate">
            {rows.map(([label, n, cr]) => (
              <div key={label} className="flex justify-between border-b border-neutral-900 px-3 py-2 text-xs">
                <span className="text-neutral-400">{label} × {n}</span><span className="font-mono text-neutral-200">{cr} cr</span>
              </div>
            ))}
            <div className="flex justify-between px-3 py-2.5 text-sm font-semibold">
              <span>Total{est ? ` · ${est.scenes} shots · ${est.runtime_seconds}s` : ''}</span>
              <span className="font-mono text-amber-300" data-testid="produce-total-credits">{est ? `${est.total_credits} cr` : '…'}</span>
            </div>
          </div>
          )}
          <ServiceNote svc={down} testId="produce-service-unavailable" />
          {estErr && <p className="rounded-md border border-red-400/30 bg-red-500/5 px-3 py-2 text-[11px] text-red-200" data-testid="produce-estimate-error">{estErr}</p>}
          {over && (
            <div className="space-y-2 rounded-md border border-amber-400/30 bg-amber-500/10 px-3 py-2.5 text-[11px] text-amber-100" data-testid="produce-over-budget">
              <p>
                This run needs ≈ ${est.needed_usd.toFixed(2)}{est.spent_usd > 0 ? ` + $${est.spent_usd.toFixed(2)} already spent = $${(est.spent_usd + est.needed_usd).toFixed(2)} in total` : ''}
                {est.fits ? ` for every shot to move — your $${est.cap_usd.toFixed(2)} budget leaves ${est.living_stills} as Living Stills.` : ` — above your $${est.cap_usd.toFixed(2)} budget.`}
                {' '}<b>Produce anyway</b> raises this run's budget to exactly that amount and never spends more; your saved budget stays as picked.
              </p>
              <Button size="sm" onClick={() => go(true)} disabled={!!starting || !!down} className="w-full bg-amber-400 text-neutral-900 hover:bg-amber-300" data-testid="produce-anyway-btn">
                <Zap size={14} className="mr-1" /> {starting === 'anyway' ? 'Starting…' : `Produce anyway — ≈ $${est.needed_usd.toFixed(2)}`}
              </Button>
            </div>
          )}
          <p className="text-[11px] text-neutral-500">Already-finished stills and renders are skipped and never charged twice. You can cancel at any time.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="produce-cancel-btn">Cancel</Button>
          <Button onClick={() => go(false)} disabled={!est || !!starting || !est.scenes || (budgetMode && !est.fits) || !!down} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="produce-confirm-btn">
            <Clapperboard size={14} className="mr-1" /> {starting === 'normal' ? 'Starting…' : budgetMode ? `Produce — ≈ $${est ? est.usd.toFixed(2) : '…'}` : `Produce — ${est?.total_credits ?? '…'} cr`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProductionBar({ project, resolution, onChanged }) {
  const [open, setOpen] = useState(false);
  const p = project.production || {};
  const running = jobRunning(p) && !jobStalled(p);
  const elapsed = useElapsed(p.started_at, running);
  const scenes = project.scenes || [];
  const anchored = new Set(scenes.flatMap((s) => s.anchor_asset_ids || []));
  const kit = (project.show_kit || []).filter((a) => a.kind !== 'product' && anchored.has(a.id));
  const counts = {
    cast: kit.filter((a) => a.reference_urls?.length).length,
    stills: scenes.filter((s) => s.still_url).length,
    motion: scenes.filter((s) => s.video_url || (s.motion_mode === 'still' && s.still_url)).length,
    living: scenes.filter((s) => s.motion_mode === 'still' && s.still_url && !s.video_url).length,
    rendering: scenes.filter(isRendering).length,
  };
  if (!scenes.length) return null;
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 backdrop-blur" data-testid="production-bar">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Step label="Script" done={scenes.length} total={scenes.length} />
          <Step label="Cast lock" done={counts.cast} total={kit.length} active={running && counts.cast < kit.length} />
          <Step label="Storyboard" done={counts.stills} total={scenes.length} active={running && counts.stills < scenes.length} />
          <Step label={counts.living ? `Motion · ${counts.living} Living Still${counts.living > 1 ? 's' : ''}` : 'Motion'} done={counts.motion} total={scenes.length} active={counts.rendering > 0} />
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <Button size="sm" variant="outline" onClick={() => cancelJob(project.id, 'production').then(onChanged)} className="border-neutral-700 text-neutral-300" data-testid="production-cancel-btn">
              <X size={13} className="mr-1" /> Cancel
            </Button>
          ) : (
            <Button onClick={() => setOpen(true)} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="produce-all-btn">
              {jobStalled(p) ? <RotateCw size={14} className="mr-1" /> : <Play size={14} className="mr-1" />}
              {jobStalled(p) ? 'Resume production' : counts.motion === scenes.length ? 'Re-produce' : 'Produce all'}
            </Button>
          )}
        </div>
      </div>
      {running && (
        <div className="mt-3 space-y-1.5" data-testid="production-progress">
          <div className="flex justify-between text-xs"><span className="text-amber-100">{p.step}</span><span className="font-mono text-neutral-400">{elapsed}</span></div>
          <Progress value={Math.round(((p.done || 0) / Math.max(1, p.total || 1)) * 100)} className="h-1.5 bg-neutral-800" />
        </div>
      )}
      {!running && p.status === 'completed' && <div className="mt-2 text-xs text-emerald-300" data-testid="production-complete">{p.step}</div>}
      {(running || p.status === 'completed') && <CostMeter production={p} />}
      {!running && p.status === 'completed' && <CostReceipt production={p} />}
      {p.errors?.length > 0 && !running && <div className="mt-1 text-[11px] text-red-300/80">{p.errors.slice(-2).join(' · ')}</div>}
      <EstimateDialog open={open} onOpenChange={setOpen} project={project} resolution={resolution} onStarted={onChanged} />
    </div>
  );
}
